import React, { useState, useEffect } from 'react';
import { createConfig, WagmiProvider, useAccount, useConnect, useDisconnect, useWalletClient, usePublicClient } from 'wagmi';
import { base } from 'wagmi/chains';
import { http } from 'viem';
import { injected, coinbaseWallet } from 'wagmi/connectors';
import { getSickleAddress, deploySickle, getStrategy, addresses, type Address } from '@sepori/sickle-abigen';
import { POOLS } from '@sepori/shared/registry';
import { encodeFunctionData, parseUnits, formatUnits } from 'viem';
import { basicPoolAbi } from '@sepori/shared/abis';

// Wagmi config for wallet connections
const wagmiConfig = createConfig({
  chains: [base],
  connectors: [
    injected(),
    coinbaseWallet({
      appName: 'Sepori Services',
    }),
  ],
  transports: {
    [base.id]: http(),
  },
});

// Pool configuration
const USDC_AERO_POOL = POOLS.USDC_AERO;
const LP_TOKEN_ADDRESS = USDC_AERO_POOL.pool as Address;
const GAUGE_ADDRESS = USDC_AERO_POOL.gauge as Address;
const USDC_ADDRESS = USDC_AERO_POOL.token0 as Address;
const AERO_ADDRESS = USDC_AERO_POOL.token1 as Address;
const ROUTER_ADDRESS = addresses.AerodromeRouter as Address;

// ERC20 ABI for approvals and balance checks
const ERC20_ABI = [
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'spender', type: 'address' },
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

function SickleTestContent() {
  const { address: walletAddress, isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const [sickleAddress, setSickleAddress] = useState<Address | null>(null);

  // Token balances and decimals
  const [usdcBalance, setUsdcBalance] = useState<string>('0');
  const [aeroBalance, setAeroBalance] = useState<string>('0');
  const [usdcDecimals, setUsdcDecimals] = useState<number>(6);
  const [aeroDecimals, setAeroDecimals] = useState<number>(18);

  // Pool reserves for ratio calculation
  const [poolReserves, setPoolReserves] = useState<{ reserve0: bigint; reserve1: bigint } | null>(null);

  // Input amounts
  const [usdcAmount, setUsdcAmount] = useState<string>('');
  const [aeroAmount, setAeroAmount] = useState<string>('');
  const [calculatedAeroAmount, setCalculatedAeroAmount] = useState<string>('');
  const [calculatedUsdcAmount, setCalculatedUsdcAmount] = useState<string>('');

  // Approval states
  const [usdcApprovalAmount, setUsdcApprovalAmount] = useState<string>('');
  const [aeroApprovalAmount, setAeroApprovalAmount] = useState<string>('');

  const [txStatus, setTxStatus] = useState<{ type: 'idle' | 'pending' | 'success' | 'error'; message?: string }>({ type: 'idle' });
  const [loading, setLoading] = useState(false);

  // Load wallet data, balances, and pool reserves
  useEffect(() => {
    if (!isConnected || !walletAddress || !publicClient) return;

    const loadData = async () => {
      try {
        // Check if Sickle wallet exists
        const sickle = await getSickleAddress(publicClient, walletAddress as Address);
        setSickleAddress(sickle);

        // Load token balances and decimals
        const [usdcBalanceResult, usdcDecimalsResult, aeroBalanceResult, aeroDecimalsResult] = await Promise.all([
          publicClient.readContract({
            address: USDC_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [walletAddress],
          }),
          publicClient.readContract({
            address: USDC_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'decimals',
          }),
          publicClient.readContract({
            address: AERO_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [walletAddress],
          }),
          publicClient.readContract({
            address: AERO_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'decimals',
          }),
        ]);

        setUsdcBalance(formatUnits(usdcBalanceResult as bigint, Number(usdcDecimalsResult)));
        setUsdcDecimals(Number(usdcDecimalsResult));
        setAeroBalance(formatUnits(aeroBalanceResult as bigint, Number(aeroDecimalsResult)));
        setAeroDecimals(Number(aeroDecimalsResult));

        // Load pool reserves for ratio calculation
        const reserves = await publicClient.readContract({
          address: LP_TOKEN_ADDRESS,
          abi: basicPoolAbi,
          functionName: 'getReserves',
        });

        setPoolReserves({
          reserve0: reserves[0] as bigint,
          reserve1: reserves[1] as bigint,
        });
      } catch (error) {
        console.error('Failed to load data:', error);
        setTxStatus({ type: 'error', message: `Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}` });
      }
    };

    loadData();
  }, [isConnected, walletAddress, publicClient]);

  // Calculate required AERO when USDC amount changes
  useEffect(() => {
    if (!poolReserves || !usdcAmount || parseFloat(usdcAmount) <= 0) {
      setCalculatedAeroAmount('');
      return;
    }

    try {
      const usdcAmountWei = parseUnits(usdcAmount, usdcDecimals);
      // Calculate: aeroAmount = (usdcAmount * reserve1) / reserve0
      const aeroAmountWei = (usdcAmountWei * poolReserves.reserve1) / poolReserves.reserve0;
      const aeroAmountFormatted = formatUnits(aeroAmountWei, aeroDecimals);
      setCalculatedAeroAmount(aeroAmountFormatted);
    } catch (error) {
      console.error('Failed to calculate AERO amount:', error);
      setCalculatedAeroAmount('');
    }
  }, [usdcAmount, poolReserves, usdcDecimals, aeroDecimals]);

  // Calculate required USDC when AERO amount changes
  useEffect(() => {
    if (!poolReserves || !aeroAmount || parseFloat(aeroAmount) <= 0) {
      setCalculatedUsdcAmount('');
      return;
    }

    try {
      const aeroAmountWei = parseUnits(aeroAmount, aeroDecimals);
      // Calculate: usdcAmount = (aeroAmount * reserve0) / reserve1
      const usdcAmountWei = (aeroAmountWei * poolReserves.reserve0) / poolReserves.reserve1;
      const usdcAmountFormatted = formatUnits(usdcAmountWei, usdcDecimals);
      setCalculatedUsdcAmount(usdcAmountFormatted);
    } catch (error) {
      console.error('Failed to calculate USDC amount:', error);
      setCalculatedUsdcAmount('');
    }
  }, [aeroAmount, poolReserves, usdcDecimals, aeroDecimals]);

  // Deploy Sickle wallet if needed
  const handleDeploySickle = async () => {
    if (!walletClient || !publicClient || !walletAddress) {
      setTxStatus({ type: 'error', message: 'Wallet not connected' });
      return;
    }

    try {
      setLoading(true);
      setTxStatus({ type: 'pending', message: 'Deploying Sickle wallet...' });

      const sickle = await deploySickle(
        walletClient,
        publicClient,
        walletAddress as Address
      );

      setSickleAddress(sickle);
      setTxStatus({ type: 'success', message: `Sickle wallet deployed: ${sickle}` });
    } catch (error) {
      console.error('Failed to deploy Sickle:', error);
      setTxStatus({ type: 'error', message: `Deployment failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  // Approve USDC to Sickle wallet
  const handleApproveUSDC = async () => {
    if (!walletClient || !walletAddress || !sickleAddress) {
      setTxStatus({ type: 'error', message: 'Wallet not connected or Sickle not deployed' });
      return;
    }

    const amount = usdcApprovalAmount || usdcAmount || usdcBalance;
    if (!amount || parseFloat(amount) <= 0) {
      setTxStatus({ type: 'error', message: 'Please enter a USDC approval amount' });
      return;
    }

    try {
      setLoading(true);
      setTxStatus({ type: 'pending', message: 'Approving USDC...' });

      const amountWei = parseUnits(amount, usdcDecimals);

      const hash = await walletClient.writeContract({
        address: USDC_ADDRESS,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [sickleAddress, amountWei],
        account: walletAddress,
        chain: base,
      });

      await publicClient?.waitForTransactionReceipt({ hash });
      setTxStatus({ type: 'success', message: 'USDC approval successful!' });
      setUsdcApprovalAmount('');
    } catch (error) {
      console.error('USDC approval failed:', error);
      setTxStatus({ type: 'error', message: `USDC approval failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  // Approve AERO to Sickle wallet
  const handleApproveAERO = async () => {
    if (!walletClient || !walletAddress || !sickleAddress) {
      setTxStatus({ type: 'error', message: 'Wallet not connected or Sickle not deployed' });
      return;
    }

    const amount = aeroApprovalAmount || aeroAmount || (calculatedAeroAmount || aeroBalance);
    if (!amount || parseFloat(amount) <= 0) {
      setTxStatus({ type: 'error', message: 'Please enter an AERO approval amount' });
      return;
    }

    try {
      setLoading(true);
      setTxStatus({ type: 'pending', message: 'Approving AERO...' });

      const amountWei = parseUnits(amount, aeroDecimals);

      const hash = await walletClient.writeContract({
        address: AERO_ADDRESS,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [sickleAddress, amountWei],
        account: walletAddress,
        chain: base,
      });

      await publicClient?.waitForTransactionReceipt({ hash });
      setTxStatus({ type: 'success', message: 'AERO approval successful!' });
      setAeroApprovalAmount('');
    } catch (error) {
      console.error('AERO approval failed:', error);
      setTxStatus({ type: 'error', message: `AERO approval failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  // Deposit USDC and AERO to pool via FarmStrategy with zap
  const handleDeposit = async () => {
    if (!walletClient || !publicClient || !walletAddress || !sickleAddress) {
      setTxStatus({ type: 'error', message: 'Wallet not connected or Sickle not deployed' });
      return;
    }

    // Determine which amounts to use
    let finalUsdcAmount: bigint;
    let finalAeroAmount: bigint;

    if (usdcAmount && parseFloat(usdcAmount) > 0) {
      finalUsdcAmount = parseUnits(usdcAmount, usdcDecimals);
      if (calculatedAeroAmount && parseFloat(calculatedAeroAmount) > 0) {
        finalAeroAmount = parseUnits(calculatedAeroAmount, aeroDecimals);
      } else {
        setTxStatus({ type: 'error', message: 'Please wait for AERO amount calculation' });
        return;
      }
    } else if (aeroAmount && parseFloat(aeroAmount) > 0) {
      finalAeroAmount = parseUnits(aeroAmount, aeroDecimals);
      if (calculatedUsdcAmount && parseFloat(calculatedUsdcAmount) > 0) {
        finalUsdcAmount = parseUnits(calculatedUsdcAmount, usdcDecimals);
      } else {
        setTxStatus({ type: 'error', message: 'Please wait for USDC amount calculation' });
        return;
      }
    } else {
      setTxStatus({ type: 'error', message: 'Please enter USDC or AERO amount' });
      return;
    }

    try {
      setLoading(true);
      setTxStatus({ type: 'pending', message: 'Depositing tokens and adding liquidity...' });

      const strategy = getStrategy(publicClient, 'FarmStrategy');

      // Apply 0.5% slippage tolerance (minimum amounts)
      const slippageBps = 50n; // 0.5%
      const usdcMin = (finalUsdcAmount * (10000n - slippageBps)) / 10000n;
      const aeroMin = (finalAeroAmount * (10000n - slippageBps)) / 10000n;

      // Build FarmStrategy.deposit with zap
      // Use deposit() for first-time deposits (handles PositionSettings, approved, referralCode)
      const depositData = encodeFunctionData({
        abi: strategy.abi,
        functionName: 'deposit',
        args: [
          {
            farm: {
              stakingContract: GAUGE_ADDRESS,
              poolIndex: 0n,
            },
            tokensIn: [USDC_ADDRESS, AERO_ADDRESS],
            amountsIn: [finalUsdcAmount, finalAeroAmount],
            zap: {
              swaps: [], // No swaps needed - providing both tokens
              addLiquidityParams: {
                router: ROUTER_ADDRESS,
                lpToken: LP_TOKEN_ADDRESS,
                tokens: [USDC_ADDRESS, AERO_ADDRESS],
                desiredAmounts: [finalUsdcAmount, finalAeroAmount],
                minAmounts: [usdcMin, aeroMin],
                extraData: '0x' as `0x${string}`,
              },
            },
            extraData: '0x' as `0x${string}`,
          },
          {
            rewardSettings: '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`,
            exitSettings: '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`,
          },
          [], // sweepTokens
          walletAddress as Address, // approved
          '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`, // referralCode
        ],
      });

      // Execute multicall via Sickle
      const hash = await walletClient.writeContract({
        address: sickleAddress,
        abi: [
          {
            inputs: [
              {
                components: [
                  { internalType: 'address', name: 'target', type: 'address' },
                  { internalType: 'bytes', name: 'callData', type: 'bytes' },
                ],
                internalType: 'struct Multicall.Call[]',
                name: 'calls',
                type: 'tuple[]',
              },
            ],
            name: 'multicall',
            outputs: [{ internalType: 'bytes[]', name: 'returnData', type: 'bytes[]' }],
            stateMutability: 'payable',
            type: 'function',
          },
        ],
        functionName: 'multicall',
        args: [[{
          target: strategy.address,
          callData: depositData,
        }]],
        account: walletAddress,
        chain: base,
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setTxStatus({ type: 'success', message: 'Deposit successful! Liquidity added and staked.' });
      setUsdcAmount('');
      setAeroAmount('');
      setCalculatedAeroAmount('');
      setCalculatedUsdcAmount('');

      // Refresh balances
      const [newUsdcBalance, newAeroBalance] = await Promise.all([
        publicClient.readContract({
          address: USDC_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [walletAddress],
        }),
        publicClient.readContract({
          address: AERO_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [walletAddress],
        }),
      ]);
      setUsdcBalance(formatUnits(newUsdcBalance as bigint, usdcDecimals));
      setAeroBalance(formatUnits(newAeroBalance as bigint, aeroDecimals));
    } catch (error) {
      console.error('Deposit failed:', error);
      setTxStatus({ type: 'error', message: `Deposit failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sickle-test" style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <h1>Sickle Test - USDC/AERO Basic Pool</h1>
      <p style={{ marginBottom: '2rem', color: '#666' }}>
        Enter USDC or AERO amount to add liquidity. The system will automatically calculate the required amount of the other token based on current pool ratios.
      </p>

      {/* Wallet Connection */}
      <div className="wallet-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <h2>Wallet Connection</h2>
        {!isConnected ? (
          <div>
            <p>Connect your wallet to get started</p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {connectors.map((connector) => (
                <button
                  key={connector.id}
                  onClick={() => connect({ connector })}
                  disabled={loading}
                  style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
                >
                  Connect {connector.name}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <p><strong>Wallet:</strong> {walletAddress}</p>
            <p><strong>Sickle Wallet:</strong> {sickleAddress || 'Not deployed'}</p>
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <p><strong>USDC Balance:</strong> {parseFloat(usdcBalance).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC</p>
              <p><strong>AERO Balance:</strong> {parseFloat(aeroBalance).toLocaleString(undefined, { maximumFractionDigits: 4 })} AERO</p>
            </div>
            {!sickleAddress && (
              <button
                onClick={handleDeploySickle}
                disabled={loading}
                style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Deploy Sickle Wallet
              </button>
            )}
            <button
              onClick={() => disconnect()}
              style={{ marginLeft: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Pool Info */}
      <div className="pool-info" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <h2>Pool Information</h2>
        <p><strong>Pool:</strong> {USDC_AERO_POOL.name}</p>
        <p><strong>LP Token:</strong> {LP_TOKEN_ADDRESS.slice(0, 10)}...{LP_TOKEN_ADDRESS.slice(-8)}</p>
        <p><strong>Gauge:</strong> {GAUGE_ADDRESS.slice(0, 10)}...{GAUGE_ADDRESS.slice(-8)}</p>
        <p><strong>Router:</strong> {ROUTER_ADDRESS.slice(0, 10)}...{ROUTER_ADDRESS.slice(-8)}</p>
      </div>

      {/* Token Input Section */}
      {isConnected && sickleAddress && (
        <div className="token-input-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h2>Add Liquidity</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Enter USDC or AERO amount. The system will calculate the required amount of the other token.
          </p>

          {/* USDC Input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              USDC Amount
              <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', color: '#666' }}>
                (Balance: {parseFloat(usdcBalance).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC)
              </span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                step="0.01"
                placeholder="Enter USDC amount"
                value={usdcAmount}
                onChange={(e) => {
                  setUsdcAmount(e.target.value);
                  setAeroAmount(''); // Clear AERO when USDC is entered
                }}
                disabled={loading || !!aeroAmount}
                style={{ padding: '0.5rem', flex: 1 }}
              />
              <button
                onClick={() => setUsdcAmount(usdcBalance)}
                disabled={loading || !!aeroAmount}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Max
              </button>
            </div>
            {calculatedAeroAmount && usdcAmount && (
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9em' }}>
                Required AERO: <strong>{parseFloat(calculatedAeroAmount).toLocaleString(undefined, { maximumFractionDigits: 4 })} AERO</strong>
              </p>
            )}
          </div>

          {/* AERO Input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              AERO Amount
              <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', color: '#666' }}>
                (Balance: {parseFloat(aeroBalance).toLocaleString(undefined, { maximumFractionDigits: 4 })} AERO)
              </span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                step="0.0001"
                placeholder="Enter AERO amount"
                value={aeroAmount}
                onChange={(e) => {
                  setAeroAmount(e.target.value);
                  setUsdcAmount(''); // Clear USDC when AERO is entered
                }}
                disabled={loading || !!usdcAmount}
                style={{ padding: '0.5rem', flex: 1 }}
              />
              <button
                onClick={() => setAeroAmount(aeroBalance)}
                disabled={loading || !!usdcAmount}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Max
              </button>
            </div>
            {calculatedUsdcAmount && aeroAmount && (
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9em' }}>
                Required USDC: <strong>{parseFloat(calculatedUsdcAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC</strong>
              </p>
            )}
          </div>

          {/* Deposit Button */}
          <div style={{ marginTop: '1rem' }}>
            <button
              onClick={handleDeposit}
              disabled={loading || (!usdcAmount && !aeroAmount)}
              style={{
                padding: '0.75rem 2rem',
                cursor: loading || (!usdcAmount && !aeroAmount) ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                backgroundColor: loading || (!usdcAmount && !aeroAmount) ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              {loading ? 'Processing...' : 'Deposit & Add Liquidity'}
            </button>
          </div>
        </div>
      )}

      {/* Approval Section */}
      {isConnected && sickleAddress && (
        <div className="approval-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h2>Token Approvals</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Approve tokens to your Sickle wallet before depositing. You can approve specific amounts or use the deposit amounts above.
          </p>

          {/* USDC Approval */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Approve USDC
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                step="0.01"
                placeholder={usdcAmount || `Max: ${usdcBalance}`}
                value={usdcApprovalAmount}
                onChange={(e) => setUsdcApprovalAmount(e.target.value)}
                disabled={loading}
                style={{ padding: '0.5rem', flex: 1 }}
              />
              <button
                onClick={handleApproveUSDC}
                disabled={loading}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Approve USDC
              </button>
              <button
                onClick={() => setUsdcApprovalAmount(usdcBalance)}
                disabled={loading}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Max
              </button>
            </div>
          </div>

          {/* AERO Approval */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Approve AERO
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                step="0.0001"
                placeholder={calculatedAeroAmount || aeroAmount || `Max: ${aeroBalance}`}
                value={aeroApprovalAmount}
                onChange={(e) => setAeroApprovalAmount(e.target.value)}
                disabled={loading}
                style={{ padding: '0.5rem', flex: 1 }}
              />
              <button
                onClick={handleApproveAERO}
                disabled={loading}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Approve AERO
              </button>
              <button
                onClick={() => setAeroApprovalAmount(aeroBalance)}
                disabled={loading}
                style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
              >
                Max
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Status */}
      {txStatus.type !== 'idle' && (
        <div
          className={`tx-status ${txStatus.type}`}
          style={{
            padding: '1rem',
            borderRadius: '8px',
            backgroundColor:
              txStatus.type === 'success'
                ? '#d4edda'
                : txStatus.type === 'error'
                ? '#f8d7da'
                : '#d1ecf1',
            color:
              txStatus.type === 'success'
                ? '#155724'
                : txStatus.type === 'error'
                ? '#721c24'
                : '#0c5460',
            marginTop: '1rem',
          }}
        >
          <strong>{txStatus.type.toUpperCase()}:</strong> {txStatus.message}
        </div>
      )}

      {loading && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <div className="spinner" style={{ display: 'inline-block' }}></div>
          <p>Processing transaction...</p>
        </div>
      )}
    </div>
  );
}

export const SickleTest: React.FC = () => {
  return (
    <WagmiProvider config={wagmiConfig}>
      <SickleTestContent />
    </WagmiProvider>
  );
};
